import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CompetencyTypesService } from 'app/_services/competency-types.service';
import { NotificationService } from 'app/_services/notification.service';
import { WorkOrderRequiredCompetencyService } from 'app/_services/work-order-required-competency.service';

@Component({
  selector: 'edit-competencies',
  templateUrl: './edit-competencies.component.html',
})
export class EditCompetenciesComponent implements OnInit {
  @Input() workOrder: any;
  @Input() procedure: any;
  @Input() siteOwnerId: number;
  @Input() workerLevel: 'LEAD' | 'ALL';
  @Input() isOemProcedureMode = false;

  public form: FormGroup;
  public allCompetencies: any[] = [];
  public competencyOptions: any[] = [];
  public workerLevelLabel = 'Lead Worker';
  public modalTitle = 'Add Work Order Competencies';
  public oemOptions: any[] = [];
  public modelOptions: any[] = [];
  public ratingOptions: any[] = [];
  public showTurbineFields = false;
  public isManufacturerLoading = false;
  public isModelAndRatingLoading = false;
  public isSubmitting = false;
  public isProcedureMode = false;

  private existingCompetencyIds: number[] = [];
  private hasExistingTurbineCompetency = false;
  private competencyOptionsLoaded = false;
  private existingCompetenciesLoaded = false;
  private manufacturerLoadVersion = 0;
  private modelRatingLoadVersion = 0;

  constructor(
    private activeModal: NgbActiveModal,
    private competencyService: CompetencyTypesService,
    private requiredCompetencyService: WorkOrderRequiredCompetencyService,
    private notificationService: NotificationService,
    private formBuilder: FormBuilder,
  ) { }

  ngOnInit() {
    this.isProcedureMode = !!this.procedure;
    const resolvedLevel = this.workerLevel === 'ALL' ? 'ALL' : 'LEAD';
    this.workerLevel = resolvedLevel;
    this.workerLevelLabel = resolvedLevel === 'ALL' ? 'All Workers' : 'Lead Worker';
    if (this.isOemProcedureMode) {
      this.modalTitle = 'Add OEM Procedure Competency';
    } else if (this.isProcedureMode) {
      this.modalTitle = 'Add Procedure Competencies';
    } else {
      this.modalTitle = 'Add Work Order Competencies';
    }

    this.form = this.formBuilder.group({
      workerLevel: new FormControl(this.workerLevel),
      competency: new FormControl(null, [Validators.required]),
      oem: new FormControl(null),
      model: new FormControl(this.isProcedureMode ? [] : null),
      rating: new FormControl(this.isProcedureMode ? [] : null),
    });

    this.initializeDisabledState();

    this.loadCompetencyOptions();
    this.loadExistingRequiredCompetencies();
    this.setupFormSubscriptions();
  }

  public closeModal() {
    this.activeModal.dismiss();
  }

  public onSubmit() {
    if (this.isSubmitting) {
      return;
    }

    const values = this.form.value;
    const selectedCompetencyId = this.getSelectedCompetencyId(values.competency);

    if (!selectedCompetencyId) {
      return;
    }

    const selectedCompetency = this.allCompetencies.find((competency) => Number(competency.id) === selectedCompetencyId);
    const isTurbine = selectedCompetency && selectedCompetency.type === 'TURBINE';

    const applyToAllWorkers = this.workerLevel === 'ALL';
    if (this.isProcedureMode) {
      const result: any = {
        competencyTypeId: selectedCompetencyId,
        applyToParty: applyToAllWorkers,
        isTurbine: !!isTurbine,
        type: selectedCompetency && selectedCompetency.type,
        shortName: selectedCompetency && selectedCompetency.shortName,
        value: selectedCompetency && selectedCompetency.value,
        competencyType: selectedCompetency ? {
          id: selectedCompetency.id,
          shortName: selectedCompetency.shortName,
          value: selectedCompetency.value,
          type: selectedCompetency.type,
        } : null,
      };

      if (isTurbine) {
        result.manufacturer = values.oem && values.oem.value ? values.oem.value : '';
        result.models = this.getSelectedOptionTexts(values.model);
        result.ratings = this.getSelectedOptionTexts(values.rating);
      }

      this.activeModal.close({
        ...result,
      });
      return;
    }

    const body: any = {
      workOrderId: this.workOrder.id,
      competencyTypeId: selectedCompetencyId,
      applyToParty: applyToAllWorkers,
    };

    if (isTurbine) {
      body.manufacturer = values.oem && values.oem.value ? values.oem.value : '';
      body.model = this.getOptionText(values.model);
      body.rating = this.getOptionText(values.rating);
    }

    this.isSubmitting = true;
    this.requiredCompetencyService.createRequiredCompetency(body).subscribe(() => {
      this.notificationService.notify('success', 'Updated!', 'Work Order competency has been updated.');
      this.activeModal.close('refresh');
    }, (error) => {
      this.isSubmitting = false;
      this.notificationService.notify('danger', 'Error', this.getErrorMessage(error, 'Could not update work order competency'));
    });
  }

  private updateTurbineFields(): void {
    const selectedCompetencyValue = this.form.get('competency').value || null;
    const selectedCompetencyId = this.getSelectedCompetencyId(selectedCompetencyValue);
    const selectedCompetency = this.allCompetencies.find((competency) => Number(competency.id) === selectedCompetencyId);

    const isTurbine = selectedCompetency && selectedCompetency.type === 'TURBINE';
    this.showTurbineFields = !!isTurbine;

    if (!this.showTurbineFields) {
      this.clearTurbineFields();
      return;
    }

    this.initializeTurbineDropdownDefaults();
    this.prefillTurbineFieldsFromAsset();

    if (this.oemOptions.length <= 1) {
      this.loadManufacturers();
    }
  }

  private loadCompetencyOptions(): void {
    const resolvedSiteOwnerId = this.siteOwnerId || (this.workOrder && this.workOrder.asset && this.workOrder.asset.site && this.workOrder.asset.site.siteOwner.id);
    const competencyType = this.isOemProcedureMode
      ? 'WORKER'
      : (this.workerLevel === 'ALL' ? 'GENERAL_SITE' : 'WORKER');

    this.competencyService
      .getAllCompetencyTypesByTypeFilteredForSiteOwner(competencyType, resolvedSiteOwnerId)
      .subscribe((data: any) => {
        this.allCompetencies = Array.isArray(data) ? data : [];

        this.competencyOptionsLoaded = true;
        this.finaliseCompetencyDropdownSetup();
      });
  }

  private setupFormSubscriptions(): void {
    this.form.get('competency').valueChanges.subscribe(() => {
      this.updateTurbineFields();
    });

    this.form.get('oem').valueChanges.subscribe((option) => {
      this.handleOemSelectionChange(option);
    });
  }

  private initializeDisabledState(): void {
    this.form.get('competency').disable();
    this.form.get('oem').disable();
    this.form.get('model').disable();
    this.form.get('rating').disable();
  }

  private loadExistingRequiredCompetencies(): void {
    if (this.isProcedureMode) {
      const existing = Array.isArray(this.procedure && this.procedure.competencies) && this.procedure.competencies.length
        ? this.procedure.competencies
        : (Array.isArray(this.procedure && this.procedure.competencyTypes) ? this.procedure.competencyTypes : []);
      this.existingCompetencyIds = Array.from(new Set<number>(existing.map((item) => Number(item.competencyTypeId || item.id)).filter((id) => !!id)));
      this.hasExistingTurbineCompetency = existing.some((item) => this.isTurbineCompetency(item));
      this.existingCompetenciesLoaded = true;
      this.finaliseCompetencyDropdownSetup();
      return;
    }

    if (!this.workOrder || !this.workOrder.id) {
      return;
    }

    this.requiredCompetencyService.getByWorkOrder(this.workOrder.id).subscribe(

      (data: any[]) => {
      const required = Array.isArray(data) ? data : [];
      this.existingCompetencyIds = Array.from(new Set(required.map((item) => Number(item.competencyTypeId)).filter((id) => !!id)));
      this.hasExistingTurbineCompetency = required.some((item) => this.isTurbineCompetency(item));

      this.existingCompetenciesLoaded = true;
      this.finaliseCompetencyDropdownSetup();
    }, (error) => {
      this.notificationService.notify('danger', 'Error', 'Could not load existing competencies for this work order');
    });
  }

  private finaliseCompetencyDropdownSetup(): void {
    if (!this.competencyOptionsLoaded || !this.existingCompetenciesLoaded) {
      return;
    }
    this.buildCompetencyOptions();
    this.form.get('competency').enable();
  }

  private buildCompetencyOptions(): void {
    this.competencyOptions = this.allCompetencies
      .map((competency) => ({
        label: competency.shortName ? `${competency.shortName} - ${competency.value}` : competency.value,
        value: competency.id,
        type: competency.type,
        disabled: this.existingCompetencyIds.includes(competency.id)
          || (competency.type === 'TURBINE' && this.hasExistingTurbineCompetency),
      }));
  }

  private isTurbineCompetency(competency: any): boolean {
    const type = competency && competency.type;

    if (type === 'TURBINE') {
      return true;
    }

    return !!(competency && (competency.manufacturer || competency.oem));
  }

  private loadManufacturers(): void {
    if (this.isManufacturerLoading) {
      return;
    }

    const requestVersion = ++this.manufacturerLoadVersion;
    this.isManufacturerLoading = true;
    this.form.get('oem').disable();

    this.requiredCompetencyService.getTurbineManufacturers().subscribe((data: any[]) => {
      if (requestVersion !== this.manufacturerLoadVersion) {
        // returns early if the screen has changed and no longer needs this response
        return;
      }

      const manufacturers = (Array.isArray(data) ? data : [])
        .map((item) => this.normalizeText((item && item.manufacturer) || (item && item.name) || item))
        .filter((item) => !!item);

      const selectedOem = this.getOptionText(this.form.get('oem').value);
      if (selectedOem) {
        manufacturers.push(selectedOem);
      }

      const unique = Array.from(new Set(manufacturers)).sort((a, b) => a.localeCompare(b));
      this.oemOptions = [this.createOptionalDropdownOption(), ...unique.map((manufacturer) => this.mapToDropdownOption(manufacturer))];
      this.isManufacturerLoading = false;
      this.form.get('oem').enable();
    }, (error) => {
      if (requestVersion !== this.manufacturerLoadVersion) {
        return;
      }
      this.notificationService.notify('danger', 'Error', 'Could not load OEM list');
      this.isManufacturerLoading = false;
    });
  }

  private handleOemSelectionChange(selectedOemOption: any): void {
    if (!this.showTurbineFields) {
      return;
    }

    const hasSelectedOem = !!(selectedOemOption && selectedOemOption.value);
    if (!hasSelectedOem) {
      this.modelRatingLoadVersion++;
      this.isModelAndRatingLoading = false;
      this.resetAndDisableModelAndRating();
      return;
    }

    if (this.isProcedureMode) {
      this.modelOptions = [];
      this.ratingOptions = [];
      this.form.patchValue({ model: [], rating: [] });
    } else {
      const optional = this.createOptionalDropdownOption();
      this.modelOptions = [optional];
      this.ratingOptions = [optional];
      this.form.patchValue({ model: optional, rating: optional });
    }
    this.loadModelAndRatingForOem(selectedOemOption.value);
  }

  private loadModelAndRatingForOem(manufacturer: string, preferredModels?: string[], preferredRatings?: string[]): void {
    if (!manufacturer) {
      return;
    }

    const requestVersion = ++this.modelRatingLoadVersion;
    this.isModelAndRatingLoading = true;
    this.form.get('model').disable();
    this.form.get('rating').disable();

    this.requiredCompetencyService.searchTurbines({ manufacturer }).subscribe((data: any[]) => {
      if (requestVersion !== this.modelRatingLoadVersion) {
        return;
      }

      const items = Array.isArray(data) ? data : [];
      const models = items.map((item) => this.normalizeText(item.model)).filter((item) => !!item);
      const ratings = items.map((item) => this.normalizeText(item.rating)).filter((item) => !!item);

      (preferredModels || []).forEach((model) => models.push(model));
      (preferredRatings || []).forEach((rating) => ratings.push(rating));

      const uniqueModels = Array.from(new Set(models)).sort((a, b) => a.localeCompare(b));
      const uniqueRatings = Array.from(new Set(ratings)).sort((a, b) => a.localeCompare(b));

      const modelDropdownOptions = uniqueModels.map((model) => this.mapToDropdownOption(model));
      const ratingDropdownOptions = uniqueRatings.map((rating) => this.mapToDropdownOption(rating));
      this.modelOptions = this.isProcedureMode ? modelDropdownOptions : [this.createOptionalDropdownOption(), ...modelDropdownOptions];
      this.ratingOptions = this.isProcedureMode ? ratingDropdownOptions : [this.createOptionalDropdownOption(), ...ratingDropdownOptions];

      if ((preferredModels && preferredModels.length) || (preferredRatings && preferredRatings.length)) {
        this.form.patchValue({
          model: this.isProcedureMode
            ? this.mapToDropdownOptions(preferredModels || [])
            : ((preferredModels && preferredModels.length) ? this.mapToDropdownOption(preferredModels[0]) : this.createOptionalDropdownOption()),
          rating: this.isProcedureMode
            ? this.mapToDropdownOptions(preferredRatings || [])
            : ((preferredRatings && preferredRatings.length) ? this.mapToDropdownOption(preferredRatings[0]) : this.createOptionalDropdownOption()),
        }, { emitEvent: false });
      }
      this.isModelAndRatingLoading = false;
      this.enableModelAndRating();
    }, (error) => {
      if (requestVersion !== this.modelRatingLoadVersion) {
        return;
      }
      this.isModelAndRatingLoading = false;
      this.notificationService.notify('danger', 'Error', 'Could not load model and rating options');
    });
  }

  private clearTurbineFields(): void {
    this.manufacturerLoadVersion++;
    this.isManufacturerLoading = false;
    this.modelRatingLoadVersion++;
    this.isModelAndRatingLoading = false;

    // for clean forms on submit, clearing of values when hiding the fields
    this.form.patchValue({ oem: null, model: this.isProcedureMode ? [] : null, rating: this.isProcedureMode ? [] : null });
    this.oemOptions = [];
    this.modelOptions = [];
    this.ratingOptions = [];
    this.form.get('oem').disable();
    this.form.get('model').disable();
    this.form.get('rating').disable();
  }

  private mapToDropdownOption(value: string) {
    return { label: value, value: value };
  }

  private mapToDropdownOptions(values: string[]) {
    return (values || []).filter((value) => !!value).map((value) => this.mapToDropdownOption(value));
  }

  private getSelectedCompetencyId(competencyFormValue: any): number | null {
    if (!competencyFormValue) {
      return null;
    }
    return competencyFormValue;
  }

  private getErrorMessage(error: any, fallback: string): string {
    const payload = error && error.error ? error.error : error;
    if (!payload) {
      return fallback;
    }

    const backendMessage = payload.detail || payload.message || payload.title || '';
    if (backendMessage === 'Can only add competency to Available Work Order') {
      return 'Can only add competency to Available Work Order';
    }

    return fallback;
  }

  private createOptionalDropdownOption() {
    return { label: 'Optional', value: null };
  }

  private resetAndDisableModelAndRating(): void {
    if (this.isProcedureMode) {
      this.modelOptions = [];
      this.ratingOptions = [];
      this.form.patchValue({ model: [], rating: [] });
    } else {
      const optional = this.createOptionalDropdownOption();
      this.modelOptions = [optional];
      this.ratingOptions = [optional];
      this.form.patchValue({ model: optional, rating: optional });
    }
    this.form.get('model').disable();
    this.form.get('rating').disable();
  }

  private enableModelAndRating(): void {
    this.form.get('model').enable();
    this.form.get('rating').enable();
  }

  private initializeTurbineDropdownDefaults(): void {
    if (!this.oemOptions.length) {
      this.oemOptions = [this.createOptionalDropdownOption()];
    }
    if (!this.modelOptions.length) {
      this.modelOptions = this.isProcedureMode ? [] : [this.createOptionalDropdownOption()];
    }
    if (!this.ratingOptions.length) {
      this.ratingOptions = this.isProcedureMode ? [] : [this.createOptionalDropdownOption()];
    }
    if (!this.form.get('oem').value) {
      this.form.patchValue({
        oem: this.createOptionalDropdownOption(),
        model: this.isProcedureMode ? [] : this.createOptionalDropdownOption(),
        rating: this.isProcedureMode ? [] : this.createOptionalDropdownOption(),
      });
    }
  }

  private prefillTurbineFieldsFromAsset(): void {
    if (this.isProcedureMode) {
      return;
    }

    const currentOem = this.getOptionText(this.form.get('oem').value);
    if (currentOem) {
      // only prefills if the value is empty
      return;
    }

    const asset = this.workOrder && this.workOrder.asset ? this.workOrder.asset : null;
    if (!asset) {
      return;
    }

    const oem = this.normalizeText(asset.manufacturer);
    const model = this.normalizeText(asset.model);
    const rating = this.normalizeText(asset.rating);

    if (!oem && !model && !rating) {
      return;
    }

    if (oem) {
      const selectedOemOption = this.mapToDropdownOption(oem);
      this.oemOptions = [this.createOptionalDropdownOption(), selectedOemOption];
      this.form.patchValue({ oem: selectedOemOption }, { emitEvent: false });
      this.form.get('oem').enable();
      this.loadModelAndRatingForOem(oem, model ? [model] : [], rating ? [rating] : []);
      return;
    }

    this.modelOptions = [this.createOptionalDropdownOption(), ...(model ? [this.mapToDropdownOption(model)] : [])];
    this.ratingOptions = [this.createOptionalDropdownOption(), ...(rating ? [this.mapToDropdownOption(rating)] : [])];
    this.form.patchValue({
      model: model ? this.mapToDropdownOption(model) : this.createOptionalDropdownOption(),
      rating: rating ? this.mapToDropdownOption(rating) : this.createOptionalDropdownOption(),
    }, { emitEvent: false });
  }

  private getOptionText(option: any): string {
    if (!option || option.value === null || option.value === undefined) {
      return '';
    }
    return this.normalizeText(option.value);
  }

  private getSelectedOptionTexts(options: any): string[] {
    if (!Array.isArray(options)) {
      return [];
    }

    return options
      .map((option) => this.getOptionText(option))
      .filter((option) => !!option);
  }

  private normalizeText(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    return value.toString().trim();
  }

}
